import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError, type OrgUser } from '../lib/api';
import { roleBadgeVariant } from '../components/Layout';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PageLoader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '../components/ui';

export function TeamPage(): JSX.Element {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['org-users'],
    queryFn: () => api.get<OrgUser[]>('/org/users', token),
  });
  const [error, setError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [busyId, setBusyId] = useState('');

  async function act(fn: () => Promise<unknown>, id = ''): Promise<void> {
    setError('');
    setBusyId(id);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['org-users'] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error');
    } finally {
      setBusyId('');
    }
  }

  async function onInvite(e: FormEvent): Promise<void> {
    e.preventDefault();
    setInviteToken('');
    await act(async () => {
      const r = await api.post<{ token: string }>('/org/invitations', { email: inviteEmail, role: 'user' }, token);
      setInviteToken(r.token);
      setInviteEmail('');
    });
  }

  if (isLoading) return <PageLoader />;
  if (isError) return <Alert kind="error">{t('team.loadError')}</Alert>;

  const statusVariant = (s: string): 'success' | 'warning' | 'default' =>
    s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'default';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('team.title')}</h1>
        <p className="text-muted-foreground">{t('team.subtitle')}</p>
      </div>
      {error && <Alert kind="error">{error}</Alert>}

      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>{t('team.email')}</TH>
              <TH>{t('team.role')}</TH>
              <TH>{t('team.status')}</TH>
              <TH className="text-right">{t('team.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {(data ?? []).map((u) => (
              <TR key={u.id}>
                <TD className="font-medium">
                  {u.email}
                  {u.id === user?.id && <span className="ml-1 text-xs text-muted-foreground">({t('team.you')})</span>}
                </TD>
                <TD>
                  <Badge variant={roleBadgeVariant(u.role)}>{t(`role.${u.role}`)}</Badge>
                </TD>
                <TD>
                  <Badge variant={statusVariant(u.status)}>{t(`status_user.${u.status}`)}</Badge>
                </TD>
                <TD className="text-right">
                  {u.id !== user?.id && u.role !== 'super_admin' && (
                    <div className="inline-flex gap-1">
                      {u.status === 'pending' && (
                        <Button size="sm" variant="secondary" disabled={busyId === u.id} onClick={() => void act(() => api.post(`/org/users/${u.id}/activate`, {}, token), u.id)}>
                          {t('team.activate')}
                        </Button>
                      )}
                      {u.status === 'active' && (
                        <Button size="sm" variant="secondary" disabled={busyId === u.id} onClick={() => void act(() => api.post(`/org/users/${u.id}/deactivate`, {}, token), u.id)}>
                          {t('team.deactivate')}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={busyId === u.id} onClick={() => void act(() => api.patch(`/org/users/${u.id}/role`, { role: u.role === 'org_admin' ? 'user' : 'org_admin' }, token), u.id)}>
                        {u.role === 'org_admin' ? t('team.demote') : t('team.promote')}
                      </Button>
                    </div>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t('team.invite')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void onInvite(e)} className="flex gap-2">
            <Input type="email" placeholder={t('team.inviteEmail')} value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
            <Button type="submit">{t('team.inviteBtn')}</Button>
          </form>
          {inviteToken && (
            <Alert kind="info" className="mt-3">
              <p className="mb-1">{t('team.inviteCreated')}</p>
              <code className="break-all text-xs">{inviteToken}</code>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
